import cx from 'classnames'
import {
  findIndex,
  get,
  intersection,
  keys,
  map,
  max,
  min,
  pick,
  slice,
  sortBy,
} from 'lodash/fp'
import PropTypes from 'prop-types'
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Link } from 'react-router-dom'

import Checkbox from '~/components/Checkbox'
import DownArrowIcon from '~/components/icons/DownArrowIcon'
import UpArrowIcon from '~/components/icons/UpArrowIcon'
import TextWithOverflow from '~/components/TextWithOverflow'
import { setAll, unsetAll } from '~/utils/object'

import cs from './table.scss'

export interface TableColumn<T> {
  name?: string
  width: number | string // string must be 'flex'
  render: (row: T, hovered: boolean) => React.ReactNode
  smallText?: boolean
  oneLine?: boolean
  omitCellPadding?: boolean
  rightAlign?: boolean
  showOnHover?: boolean
  verticalCenter?: boolean
  stopPropagation?: boolean
  disabled?: boolean
  // Not compatible with oneLine for now.
  receiveHoverParamInRender?: boolean
  // Function to extract the sortable value from a row. If provided, the column will be sortable.
  sortable?: (row: T) => string | number | Date | null | undefined
}

interface InfiniteScrollProps {
  hasMoreData: boolean
  fetchMoreData: () => void
}

export interface TableSelectableProps {
  onRowsSelected: (rowsId: { [key: TableRowId]: boolean }) => void
  selectedRowIds: { [key: TableRowId]: boolean }
}

type TableRowId = string | number

interface TableProps<T> {
  className?: string
  columns: TableColumn<T>[]
  data: T[]
  onRowClick?: (row: T, rowIndex: number) => void
  rowKey?: string | ((row: T) => TableRowId)
  noDataMessage?: ReactNode
  widthOverride?: number
  headerTopBottomPaddingVariant?:
    | 'headerTopBottomPaddingDefault'
    | 'headerTopBottomPaddingEqual'
  rowLeftRightPaddingVariant?: 'rowPaddingLow' | 'rowPaddingHigh' | 'rowPaddingNormal'
  heightSizing?: 'flex' | 'default' | 'flexAuto'
  infiniteScrollProps?: InfiniteScrollProps
  selectableProps?: TableSelectableProps
  hideRowBorders?: boolean
  flushLeft?: boolean
  disabled?: boolean
  // Applies position: sticky to the header container,
  // which can be useful for certain page layouts.
  stickyHeaders?: boolean
}

type SortDirection = 'asc' | 'desc' | null

const Table = <T extends object>(props: TableProps<T>) => {
  const rowsContainerId = useId()
  const [rowsWidth, setRowsWidth] = useState<number | null>(null)
  const [lastSelectedRowId, setLastSelectedRowId] = useState<TableRowId | null>(null)
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const rowsRef = useRef<HTMLDivElement>(null)

  const measureCurrentRowsWidth = useCallback(() => {
    if (rowsRef.current) {
      // Exclude the width of the scrollbars.
      setRowsWidth(rowsRef.current.clientWidth)
    }
  }, [])

  const getTableRowId = useCallback(
    (row: T): TableRowId => {
      const { rowKey } = props
      if (!rowKey) {
        return findIndex(r => r === row, props.data)
      }
      if (rowKey instanceof Function) {
        return rowKey(row)
      }
      return get(rowKey, row)
    },
    [props],
  )

  useEffect(() => {
    measureCurrentRowsWidth()
    window.addEventListener('resize', measureCurrentRowsWidth)
    return () => window.removeEventListener('resize', measureCurrentRowsWidth)
  }, [measureCurrentRowsWidth])

  useEffect(() => {
    const { selectableProps, data: propsData } = props
    if (!selectableProps) return
    const selectedIds = keys(selectableProps.selectedRowIds)
    const allRowIds = map(row => String(getTableRowId(row)), propsData)

    const newSelectedIds = intersection(selectedIds, allRowIds)
    if (newSelectedIds.length === selectedIds.length) return
    const newSelectedRowIds = pick(newSelectedIds, selectableProps.selectedRowIds)
    selectableProps.onRowsSelected(newSelectedRowIds)
  }, [props.data, props.selectableProps?.selectedRowIds, getTableRowId, props])

  const handleRowCheckboxClicked = (event, row) => {
    const { data, selectableProps } = props
    if (!selectableProps) return
    let { selectedRowIds } = selectableProps

    const rowId = getTableRowId(row)

    let rowIds = [rowId]

    // If the shift key is clicked, select all between currently selected and last selected.
    if (event.shiftKey && lastSelectedRowId) {
      const bounds = [
        findIndex(row => getTableRowId(row) === lastSelectedRowId, data),
        findIndex(row => getTableRowId(row) === rowId, data),
      ]
      rowIds = map(
        row => getTableRowId(row),
        slice<T>(min(bounds) as number, (max(bounds) as number) + 1, data),
      )
    }

    if (selectedRowIds[getTableRowId(row)]) {
      selectedRowIds = unsetAll(rowIds, selectedRowIds)
    } else {
      selectedRowIds = setAll(rowIds, true, selectedRowIds)
    }

    selectableProps.onRowsSelected(selectedRowIds)
    setLastSelectedRowId(getTableRowId(row))
  }

  const areAllRowsSelected = () => {
    const { data, selectableProps } = props
    if (!selectableProps) return false

    const allRowIds = map(row => String(getTableRowId(row)), data)

    const selectedIds = keys(selectableProps.selectedRowIds)

    return (
      allRowIds.length === selectedIds.length &&
      intersection(selectedIds, allRowIds).length === selectedIds.length
    )
  }

  const handleHeaderCheckboxClicked = () => {
    const { data, selectableProps } = props
    if (!selectableProps) return null

    const allSelected = areAllRowsSelected()

    if (allSelected) {
      selectableProps.onRowsSelected({})
    } else {
      const allRowIds = map(getTableRowId, data)
      const newRowIds = setAll(allRowIds, true, {})
      selectableProps.onRowsSelected(newRowIds)
    }

    setLastSelectedRowId(null)
  }

  const getRowStyle = column => {
    if (column.width === 'flex') {
      return {
        flex: '1 1 0',
        minWidth: 0,
      }
    }
    return {
      width: column.width,
    }
  }

  const getHeadersStyle = () => {
    if (rowsWidth) {
      return {
        width: rowsWidth,
      }
    }
    return {}
  }

  // This allows callers to respond to hovers.
  // This implementation feels a bit hacky, but we preferred it over an onMouseEnter/onMouseLeave approach.
  const renderCellContents = (column, row) => {
    if (column.receiveHoverParamInRender) {
      return (
        <>
          <div className={cx(cs.cellContents, cs.showOnHover)}>
            {column.render(row, true)}
          </div>
          <div className={cx(cs.cellContents, cs.hideOnHover)}>
            {column.render(row, false)}
          </div>
        </>
      )
    }

    return column.render(row, false)
  }

  const renderRow = (row, rowIndex) => {
    const { onRowClick, selectableProps, columns, hideRowBorders } = props
    const cells = columns.map((column, index) => {
      const hasLink = row._link && !column.stopPropagation
      return React.createElement(
        hasLink ? Link : 'div',
        {
          className: cx(
            cs.cell,
            column.smallText && cs.smallText,
            column.rightAlign && cs.rightAlign,
            column.omitCellPadding && cs.omitCellPadding,
            column.showOnHover && cs.showOnHover,
            column.verticalCenter && cs.verticalCenter,
          ),
          style: getRowStyle(column),
          key: `${column.name}__${index}`,
          onClick: event => column.stopPropagation && event.stopPropagation(),
          to: row._link,
        },
        column.oneLine ? (
          <TextWithOverflow
            text={column.render(row, false)}
            popoverContent={column.render(row, false)}
          />
        ) : (
          renderCellContents(column, row)
        ),
      )
    })

    const checkboxCell = selectableProps ? (
      // biome-ignore lint/a11y/useSemanticElements: Using div with role=button to maintain flex layout in table
      <div
        onClick={event => {
          if (!props.disabled) {
            handleRowCheckboxClicked(event, row)
          }
          event.stopPropagation()
        }}
        onKeyDown={event => {
          if ((event.key === 'Enter' || event.key === ' ') && !props.disabled) {
            handleRowCheckboxClicked(event, row)
            event.stopPropagation()
          }
        }}
        role='button'
        tabIndex={0}
        className={cx(cs.checkbox, cs.cell)}
      >
        <Checkbox
          checked={!!selectableProps.selectedRowIds[getTableRowId(row)]}
          className={cs.checkboxContainer}
          disabled={props.disabled}
        />
      </div>
    ) : null

    return (
      <div
        key={getTableRowId(row)}
        className={cx(
          cs.row,
          hideRowBorders && cs.hideRowBorders,
          onRowClick && cs.withOnClick,
        )}
        onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
        onKeyDown={
          onRowClick
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onRowClick(row, rowIndex)
                }
              }
            : undefined
        }
        role={onRowClick ? 'button' : undefined}
        tabIndex={onRowClick ? 0 : undefined}
      >
        {checkboxCell}
        {cells}
      </div>
    )
  }

  const handleHeaderClick = (columnIndex: number) => {
    const column = props.columns[columnIndex]
    if (!column.sortable) return

    if (sortColumnIndex === columnIndex) {
      // Toggle through: asc -> desc -> null (unsorted)
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(null)
        setSortColumnIndex(null)
      }
    } else {
      // Start sorting this column in ascending order
      setSortColumnIndex(columnIndex)
      setSortDirection('asc')
    }
  }

  const getSortedData = () => {
    const { data, columns } = props
    if (sortColumnIndex === null || sortDirection === null) {
      return data
    }

    const column = columns[sortColumnIndex]
    if (!column.sortable) {
      return data
    }

    const sorted = sortBy(column.sortable, data)
    return sortDirection === 'desc' ? sorted.reverse() : sorted
  }

  const renderBody = () => {
    const { infiniteScrollProps } = props
    const sortedData = getSortedData()
    const rows = sortedData.map(renderRow)

    if (infiniteScrollProps) {
      return (
        <InfiniteScroll
          dataLength={sortedData.length}
          next={infiniteScrollProps.fetchMoreData}
          hasMore={infiniteScrollProps.hasMoreData}
          loader={<div className={cs.infiniteloading}>Loading...</div>}
          scrollableTarget={rowsContainerId}
        >
          {rows}
        </InfiniteScroll>
      )
    }
    return rows
  }

  const {
    className,
    data,
    noDataMessage,
    widthOverride,
    heightSizing,
    rowLeftRightPaddingVariant,
    headerTopBottomPaddingVariant,
    columns,
    selectableProps,
    flushLeft,
    stickyHeaders,
  } = props

  if (data.length === 0) {
    return (
      <div
        className={cx(
          cs.table,
          className,
          rowLeftRightPaddingVariant && cs[rowLeftRightPaddingVariant],
          headerTopBottomPaddingVariant && cs[headerTopBottomPaddingVariant],
          heightSizing && cs[heightSizing],
          flushLeft && cs.flushLeft,
        )}
      >
        <div className={cs.noDataMessage}>{noDataMessage || 'No data to display'}</div>
      </div>
    )
  }

  const style = widthOverride
    ? {
        width: widthOverride,
      }
    : undefined

  return (
    <div
      className={cx(
        cs.table,
        className,
        rowLeftRightPaddingVariant && cs[rowLeftRightPaddingVariant],
        headerTopBottomPaddingVariant && cs[headerTopBottomPaddingVariant],
        heightSizing && cs[heightSizing],
        flushLeft && cs.flushLeft,
      )}
      style={style}
    >
      <div
        className={cx(cs.headers, stickyHeaders && cs.stickyHeaders)}
        style={getHeadersStyle()}
      >
        {selectableProps && (
          // biome-ignore lint/a11y/useSemanticElements: Using div with role=button to maintain flex layout in table
          <div
            onClick={() => {
              if (!props.disabled) {
                handleHeaderCheckboxClicked()
              }
            }}
            onKeyDown={event => {
              if ((event.key === 'Enter' || event.key === ' ') && !props.disabled) {
                handleHeaderCheckboxClicked()
              }
            }}
            role='button'
            tabIndex={0}
            className={cx(cs.checkbox, cs.header)}
          >
            <Checkbox
              checked={areAllRowsSelected()}
              className={cs.checkboxContainer}
              disabled={props.disabled}
            />
          </div>
        )}
        {columns.map((column, index) => {
          const isSortable = !!column.sortable
          const isSorted = sortColumnIndex === index
          const showSortIndicator = isSortable && isSorted && sortDirection

          return (
            <div
              style={getRowStyle(column)}
              key={`${column.name}__${index}`}
              className={cx(
                cs.header,
                column.rightAlign && cs.rightAlign,
                column.omitCellPadding && cs.omitCellPadding,
                isSortable && cs.sortable,
              )}
              onClick={() => isSortable && handleHeaderClick(index)}
              onKeyDown={event => {
                if (isSortable && (event.key === 'Enter' || event.key === ' ')) {
                  handleHeaderClick(index)
                }
              }}
              role={isSortable ? 'button' : undefined}
              tabIndex={isSortable ? 0 : undefined}
            >
              {isSortable ? (
                <span className={cs.headerContent}>
                  {column.name}
                  <span className={cs.sortIndicator}>
                    {showSortIndicator && sortDirection === 'asc' && (
                      <UpArrowIcon className={cs.sortIcon} />
                    )}
                    {showSortIndicator && sortDirection === 'desc' && (
                      <DownArrowIcon className={cs.sortIcon} />
                    )}
                    {!showSortIndicator && (
                      <span className={cs.sortIconPlaceholder}>
                        <UpArrowIcon className={cx(cs.sortIcon, cs.inactive)} />
                      </span>
                    )}
                  </span>
                </span>
              ) : (
                column.name
              )}
            </div>
          )
        })}
      </div>
      <div className={cs.rows} id={rowsContainerId} ref={rowsRef}>
        {renderBody()}
      </div>
    </div>
  )
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      render: PropTypes.func,
      smallText: PropTypes.bool,
      oneLine: PropTypes.bool,
      omitCellPadding: PropTypes.bool,
      rightAlign: PropTypes.bool,
      showOnHover: PropTypes.bool,
      verticalCenter: PropTypes.bool,
    }),
  ),
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  infiniteScrollProps: PropTypes.shape({
    hasMoreData: PropTypes.bool,
    fetchMoreData: PropTypes.func,
  }),
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  // The field to use for the React key prop.
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  noDataMessage: PropTypes.string,
  // Manually set the width of the table. Otherwise, the table will take on the width of
  // its parent element.
  // Useful if you have a wide table and want a horizontal scrollbar in the parent element.
  widthOverride: PropTypes.number,
  rowLeftRightPaddingVariant: PropTypes.oneOf(['rowPaddingLow', 'rowPaddingHigh']),
  // Whether to use flex to determine height.
  // If flex is used, the Table's parent element must have an explicit height.
  // flexAuto sets flex-basis to auto, allowing for more sophisticated resizing.
  // Specifically, if max-height is 300px, the rows will not expand to 300px unless needed.
  heightSizing: PropTypes.oneOf(['flex', 'default', 'flexAuto']),
  selectableProps: PropTypes.shape({
    onRowsSelected: PropTypes.func,
    selectedRowIds: PropTypes.objectOf(PropTypes.bool),
  }),
  hideRowBorders: PropTypes.bool,
  // Set a negative margin-left so the label is flush with the left.
  flushLeft: PropTypes.bool,
}

Table.defaultProps = {
  heightSizing: 'flex',
  rowLeftRightPaddingVariant: 'rowPaddingHigh',
  headerTopBottomPaddingVariant: 'headerTopBottomPaddingDefault',
}

export default Table
